import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, useRxData, useRxQuery } from "rxdb-hooks";
import { RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME } from "./epocus-billing-status";
import { RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME } from "./epocus-encounter";
import { RXDB_EPOCUS_EXAM_COLLECTION_NAME } from "./epocus-exam";
import { RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME } from "./epocus-patient-consent";
import { RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME } from "./epocus-probe-association";
import { RXDB_EPOCUS_PROGRAM_COLLECTION_NAME } from "./epocus-program";
import { RXDB_EPOCUS_REVIEW_COLLECTION_NAME } from "./epocus-review";
import { RXDB_IMAGING_INSTANCE_COLLECTION_NAME } from "./imaging-instance";
import { RXDB_IMAGING_STUDY_COLLECTION_NAME } from "./imaging-study";

export const RXDB_SYNC_CHECKPOINT_COLLECTION_NAME = "sync-checkpoint";

// Type-safe wrappers for all rxdb-hooks hooks. These should be copy-pasted for each
// collection, lest a type-wizard arrives.
// https://github.com/cvara/rxdb-hooks?tab=readme-ov-file#userxdocument
export const useRxCollectionSyncCheckpoint = () =>
  useRxCollection<SyncCheckpoint>(RXDB_SYNC_CHECKPOINT_COLLECTION_NAME);
export const useRxQuerySyncCheckpoint = (
  ...params: Parameters<typeof useRxQuery<SyncCheckpoint>>
) => useRxQuery<SyncCheckpoint>(...params);
type UseRxData = Parameters<typeof useRxData<SyncCheckpoint>>;
export const useRxDataSyncCheckpoint = (
  queryConstructor: UseRxData["1"],
  options?: UseRxData["2"],
) =>
  useRxData<SyncCheckpoint>(
    RXDB_SYNC_CHECKPOINT_COLLECTION_NAME,
    queryConstructor,
    options,
  );

export const SYNC_COLLECTIONS = [
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
  RXDB_IMAGING_STUDY_COLLECTION_NAME,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
  RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME,
  RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
  RXDB_EPOCUS_PROGRAM_COLLECTION_NAME,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
  RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME,
];

const SYNC_CHECKPOINT_SCHEMA_LITERAL = {
  title: "sync-checkpoint schema",
  version: 19,
  description: "Stores RxDB checkpoints for an entityId/collection combo.",
  type: "object",
  // Composite primary key https://rxdb.info/rx-schema.html#composite-primary-key
  primaryKey: {
    key: "id",
    fields: ["entityId", "collectionName"],
    separator: "-",
  },
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    entityId: {
      type: "string",
      maxLength: 100,
    },
    collectionName: {
      type: "string",
      enum: SYNC_COLLECTIONS,
      maxLength: 100,
    },
    checkpoint: {
      type: "object",
      properties: {
        id: {
          type: "string",
          maxLength: 100,
        },
        updatedAt: {
          type: "string",
          format: "date-time",
        },
      },
      required: ["id", "updatedAt"],
    },
  },
  required: ["id", "entityId", "collectionName", "checkpoint"],
  indexes: ["entityId", "collectionName"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;
const schemaTyped = toTypedRxJsonSchema(SYNC_CHECKPOINT_SCHEMA_LITERAL);

// aggregate the document type from the schema
export type SyncCheckpoint = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

// create the typed RxJsonSchema from the literal typed object.
export const SYNC_CHECKPOINT_SCHEMA: RxJsonSchema<SyncCheckpoint> =
  SYNC_CHECKPOINT_SCHEMA_LITERAL;
